// src/pages/PainelCliente/MinhasCampanhas.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../axiosConfig";
import { CheckCircle, Clock, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./MinhasCampanhas.css";

/**
 * MinhasCampanhas (Cliente)
 * - Lista campanhas do cliente via GET /campanhas/desempenho/:clienteId
 * - Mostra contadores (participadas / conquistadas / canceladas)
 * - Permite cancelar inscrição apenas se status === "Em andamento"
 *
 * Observação: créditos válidos = operações aprovadas + créditos manuais,
 * sempre após a inscrição e dentro do período da campanha (regra do backend).
 */

const STATUS = {
  EM_ANDAMENTO: "Em andamento",
  CONQUISTADA: "Conquistada",
  ENCERRADA: "Encerrada",
  CANCELADA: "Cancelada",
};

function formatDateBR(dateLike) {
  if (!dateLike) return "-";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR");
}

function asNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeCampanha(raw) {
  const status = String(raw?.status || STATUS.EM_ANDAMENTO).trim();
  return {
    campanha_id: raw?.campanha_id ?? raw?.id,
    titulo: raw?.titulo || "Campanha",
    data_inscricao: raw?.data_inscricao || null,
    data_inicio: raw?.data_inicio || null,
    data_fim: raw?.data_fim || null,
    pontos_objetivo: asNumber(raw?.pontos_objetivo, 0),
    pontos_acumulados: asNumber(raw?.pontos_acumulados, 0),
    recompensa: raw?.recompensa || "",
    status,
  };
}

function StatusBadge({ status }) {
  const cls = String(status || STATUS.EM_ANDAMENTO).replace(/\s+/g, "-").toLowerCase();

  return (
    <div className={`status-box ${cls}`}>
      {status === STATUS.CONQUISTADA && <CheckCircle size={18} />}
      {status === STATUS.EM_ANDAMENTO && <Clock size={18} />}
      {status === STATUS.ENCERRADA && <XCircle size={18} />}
      {status === STATUS.CANCELADA && <XCircle size={18} />}
      <span>{status}</span>
    </div>
  );
}

export default function MinhasCampanhas() {
  const navigate = useNavigate();

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || null;
    } catch {
      return null;
    }
  }, []);

  const clienteId = usuario?.id;

  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregarCampanhas = useCallback(async () => {
    if (!clienteId) {
      setCampanhas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErro("");

    try {
      const res = await axios.get(`/campanhas/desempenho/${clienteId}`);
      const lista = Array.isArray(res.data) ? res.data.map(normalizeCampanha) : [];
      setCampanhas(lista);
    } catch (e) {
      console.error("Erro ao carregar campanhas:", e?.response?.data || e);
      setErro("Não foi possível carregar suas campanhas. Tente novamente.");
      setCampanhas([]);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  const cancelarInscricao = useCallback(
    async (campanhaId) => {
      if (!clienteId) return;

      const confirmacao = window.confirm(
        "Tem certeza que deseja cancelar sua inscrição nesta campanha?"
      );
      if (!confirmacao) return;

      try {
        await axios.delete(`/campanhas/${campanhaId}/cancelar/${clienteId}`);
        // Evita alert() (mais suave e não trava UI). Se preferir, troque por toast.
        await carregarCampanhas();
      } catch (e) {
        console.error("Erro ao cancelar inscrição:", e?.response?.data || e);
        window.alert("Erro ao cancelar inscrição.");
      }
    },
    [clienteId, carregarCampanhas]
  );

  const resumo = useMemo(() => {
    const totalParticipadas = campanhas.length;
    const totalConquistadas = campanhas.filter((c) => c.status === STATUS.CONQUISTADA).length;
    const totalCanceladas = campanhas.filter((c) => c.status === STATUS.CANCELADA).length;
    return { totalParticipadas, totalConquistadas, totalCanceladas };
  }, [campanhas]);

  return (
    <div className="minhas-campanhas-container">
      <h2 className="titulo-campanhas">Minhas Campanhas</h2>

      {/* Topo: Nota + contadores */}
      <div className="bloco-campanhas-topo">
        <div className="campanha-card-topo nota">
          <p>
            <strong>Como funcionam os pontos?</strong>
          </p>
          <p>
            São considerados pontos provenientes de <strong>operações aprovadas</strong> e também de{" "}
            <strong>créditos manuais</strong>.
            <br />
            A pontuação conta apenas para créditos gerados <strong>dentro do período da campanha</strong> e{" "}
            <strong>após sua inscrição</strong>.
          </p>
        </div>

        <div className="campanha-card-topo total">
          <p className="campanha-card-titulo">Campanhas Participadas</p>
          <p className="campanha-card-numero">{resumo.totalParticipadas}</p>
        </div>

        <div className="campanha-card-topo conquistadas">
          <p className="campanha-card-titulo">Campanhas Conquistadas</p>
          <p className="campanha-card-numero">{resumo.totalConquistadas}</p>
        </div>

        <div className="campanha-card-topo canceladas">
          <p className="campanha-card-titulo">Campanhas Canceladas</p>
          <p className="campanha-card-numero">{resumo.totalCanceladas}</p>
        </div>
      </div>

      {/* Corpo */}
      {loading ? (
        <p style={{ textAlign: "center" }}>Carregando campanhas...</p>
      ) : erro ? (
        <div style={{ textAlign: "center" }}>
          <p style={{ marginBottom: 10 }}>{erro}</p>
          <button type="button" className="btn-detalhes" onClick={carregarCampanhas}>
            Tentar novamente
          </button>
        </div>
      ) : campanhas.length === 0 ? (
        <p style={{ textAlign: "center" }}>
          Você ainda não está inscrito em nenhuma campanha.
        </p>
      ) : (
        <div className="campanhas-grid">
          {campanhas.map((c) => {
            const status = c.status || STATUS.EM_ANDAMENTO;

            return (
              <div key={c.campanha_id} className="campanha-card">
                <h3>{c.titulo}</h3>

                <p>
                  <strong>Inscrição:</strong> {formatDateBR(c.data_inscricao)}
                </p>

                <p>
                  <strong>Período:</strong> {formatDateBR(c.data_inicio)} a {formatDateBR(c.data_fim)}
                </p>

                <p>
                  <strong>Meta:</strong> {c.pontos_objetivo.toLocaleString("pt-BR")} pontos
                </p>

                <p>
                  <strong>Acumulado:</strong> {c.pontos_acumulados.toLocaleString("pt-BR")} pontos
                </p>

                {c.recompensa ? (
                  <p>
                    <strong>Recompensa:</strong> {c.recompensa}
                  </p>
                ) : null}

                {/* Barra de progresso */}
                <div className="campanha-progresso">
                  <div className="campanha-progresso-track">
                    <div
                      className="campanha-progresso-fill"
                      style={{
                        width: `${Math.min(100, c.pontos_objetivo > 0
                          ? (c.pontos_acumulados / c.pontos_objetivo) * 100
                          : 0)}%`
                      }}
                    />
                  </div>
                </div>

                <StatusBadge status={status} />

                <div className="botoes-campanha">
                  <button
                    type="button"
                    className="btn-detalhes"
                    onClick={() =>
                      navigate(`/painel-cliente/campanhas/${c.campanha_id}/inscricao`)
                    }
                  >
                    Ver detalhes
                  </button>

                  {status === STATUS.EM_ANDAMENTO && (
                    <button
                      type="button"
                      className="btn-cancelar"
                      onClick={() => cancelarInscricao(c.campanha_id)}
                    >
                      Cancelar inscrição
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}